import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Space,
  message,
  Popconfirm,
  Typography,
  Card,
  Spin,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ShopOutlined,
  GlobalOutlined,
  PhoneOutlined,
  HomeOutlined
} from '@ant-design/icons';
import { shopsApi } from '../utils/api';
import PlanGate from '../components/common/PlanGate';
import usePlan from '../hooks/usePlan';

const { Title } = Typography;
const { TextArea } = Input;

const ShopManagement = () => {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingShop, setEditingShop] = useState(null);
  const [form] = Form.useForm();

  const { canCreate } = usePlan();

  // Chargement initial des boutiques
  useEffect(() => {
    fetchShops();
  }, []);

  // Fonction pour récupérer les boutiques
  const fetchShops = async () => {
    setLoading(true);
    try {
      const response = await shopsApi.getAll({ is_active: undefined }); // Récupérer actives et inactives
      setShops(response.data || []);
    } catch (error) {
      message.error('Erreur lors du chargement des boutiques');
      console.error('Fetch shops error:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  // Gérer l'ouverture de la modale (ajout ou modification)
  const showModal = async (shop = null) => {
    // Si c'est une nouvelle boutique, vérifier les limites du plan
    if (!shop) {
      try {
        const limitCheck = await canCreate('shops', shops.length);
        
        if (!limitCheck.allowed) {
          message.warning(
            `Limite atteinte: ${limitCheck.current}/${limitCheck.limit} boutique(s) autorisée(s) sur votre plan.`
          );
          return;
        }
      } catch (error) {
        console.error('Erreur lors de la vérification des limites:', error);
        message.error('Erreur lors de la vérification des limites');
        return;
      }
    }

    setEditingShop(shop);
    form.setFieldsValue(
      shop
        ? { ...shop }
        : { name: '', address: '', phone: '', website: '', is_active: true, code: '' }
    );
    setIsModalVisible(true);
  };

  // Gérer la fermeture de la modale
  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingShop(null);
    form.resetFields();
  };

  // Gérer la soumission du formulaire (ajout ou modification)
  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (editingShop) {
        // Mise à jour
        await shopsApi.update(editingShop.id, values);
        message.success('Boutique mise à jour avec succès');
      } else {
        // Création
        await shopsApi.create(values);
        message.success('Boutique créée avec succès');
      }
      fetchShops(); // Recharger la liste
      handleCancel(); // Fermer la modale
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Erreur lors de la sauvegarde de la boutique';
      message.error(errorMsg);
      console.error('Save shop error:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  // Gérer la suppression d'une boutique
  const handleDelete = async (shopId) => {
    setLoading(true);
    try {
      await shopsApi.delete(shopId);
      message.success('Boutique supprimée avec succès');
      fetchShops(); // Recharger la liste
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Erreur lors de la suppression de la boutique';
      message.error(errorMsg);
      console.error('Delete shop error:', error.response?.data || error.message);
      // Afficher une alerte spécifique si des caissiers sont liés
      if (errorMsg.includes('caissiers y sont toujours assignés')) {
          Modal.warning({
              title: 'Suppression impossible',
              content: 'Vous ne pouvez pas supprimer cette boutique car des caissiers y sont encore rattachés.',
          });
      }
    } finally {
      setLoading(false);
    }
  };

  // Définition des colonnes pour le tableau Ant Design
  const columns = [
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text) => <Typography.Text strong>{text}</Typography.Text>,
    },
    {
      title: 'Adresse',
      dataIndex: 'address',
      key: 'address',
      ellipsis: true, // Tronquer si trop long
    },
    {
      title: 'Téléphone',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Site Web',
      dataIndex: 'website',
      key: 'website',
      render: (text) => text ? <a href={text.startsWith('http') ? text : `http://${text}`} target="_blank" rel="noopener noreferrer">{text}</a> : '-',
    },
    {
      title: 'Statut',
      dataIndex: 'is_active',
      key: 'is_active',
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value, record) => record.is_active === value,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<EditOutlined />} onClick={() => showModal(record)}>Modifier</Button>
          <Popconfirm
            title="Êtes-vous sûr de vouloir supprimer cette boutique ?"
            onConfirm={() => handleDelete(record.id)}
            okText="Oui"
            cancelText="Non"
            placement="left"
          >
            <Button icon={<DeleteOutlined />} danger>Supprimer</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Gestion des Boutiques</Title>
      <Card>
        {/* Bouton Ajouter une boutique avec contrôle de limitation */}
        <PlanGate 
          type="limit" 
          limitType="shops" 
          currentCount={shops.length}
          fallback={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled
              style={{ marginBottom: 16 }}
              className="opacity-50"
            >
              Limite de boutiques atteinte
            </Button>
          }
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showModal()}
            style={{ marginBottom: 16 }}
          >
            Ajouter une boutique
          </Button>
        </PlanGate>

        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={shops}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} de ${total} boutiques`,
            }}
          />
        </Spin>
      </Card>

      {/* Modal pour ajouter/modifier une boutique */}
      <Modal
        title={editingShop ? 'Modifier la boutique' : 'Ajouter une boutique'}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={editingShop ? 'Modifier' : 'Ajouter'}
        cancelText="Annuler"
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          name="shopForm"
        >
          <Form.Item
            label="Nom de la boutique"
            name="name"
            rules={[
              { required: true, message: 'Veuillez saisir le nom de la boutique' },
              { min: 2, message: 'Le nom doit contenir au moins 2 caractères' },
            ]}
          >
            <Input prefix={<ShopOutlined />} placeholder="Nom de la boutique" />
          </Form.Item>

          <Form.Item
            label="Code de la boutique"
            name="code"
            rules={[
              { required: true, message: 'Veuillez saisir le code de la boutique' },
              { min: 2, message: 'Le code doit contenir au moins 2 caractères' },
              { max: 20, message: 'Le code ne peut pas dépasser 20 caractères' },
            ]}
          >
            <Input placeholder="Code unique de la boutique (ex: BTQ001)" />
          </Form.Item>

          <Form.Item
            label="Adresse"
            name="address"
            rules={[
              { min: 5, message: 'L\'adresse doit contenir au moins 5 caractères' },
            ]}
          >
            <TextArea prefix={<HomeOutlined />} placeholder="Adresse complète de la boutique" rows={3} />
          </Form.Item>

          <Form.Item
            label="Téléphone"
            name="phone"
            rules={[
              { pattern: /^[+]?[\d\s\-()]+$/, message: 'Format de téléphone invalide' },
            ]}
          >
            <Input prefix={<PhoneOutlined />} placeholder="Numéro de téléphone" />
          </Form.Item>

          <Form.Item
            label="Site Web"
            name="website"
            rules={[
              { type: 'url', message: 'Veuillez saisir une URL valide' },
            ]}
          >
            <Input prefix={<GlobalOutlined />} placeholder="https://www.exemple.com" />
          </Form.Item>

          <Form.Item
            label="Statut"
            name="is_active"
            valuePropName="checked"
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ShopManagement; 